import { ActionIcon, Flex, Text } from '@mantine/core';
import { DealEventSource } from '@dealstack/shared/lib/backendClient';
import type { DealEventTct } from '@dealstack/shared/lib/backendClient';
import { I18Namespaces } from '@dealstack/shared/lib/common/i18n/i18n.ts';
import { DATE_FORMATS } from '@dealstack/shared/lib/format/dates/date.ts';
import { Icon } from '@dealstack/frontend/shared/components/fundament/Icon';
import { TranslateFunction } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.types';
import { createTextColumn } from '@dealstack/frontend/shared/components/Table/MantineTable/ColumnDefs/TextColumn.tsx';
import { createDateColumn } from '@dealstack/frontend/shared/components/Table/MantineTable/ColumnDefs/DateColumn.tsx';
import { DateRangePickerFilter } from '@dealstack/frontend/shared/components/Table/MantineTable/Filters/DateRangePickerFilter';
import {
	AsyncFilterMultiSelectProps,
	ExtendedColumnDef,
} from '@dealstack/frontend/shared/components/Table/MantineTable/useMantineTableWithPaginationFilteringSorting';
import {
	formatActorName,
	formatDealEventHeading,
} from '@dealstack/frontend/app/pages/Project/DealEventLog/dealEventLog.utils.ts';
import { DealEventLogTab } from '@dealstack/frontend/app/pages/Project/DealEventLog/DealEventLog.types.ts';
import { DealEventStatusCell } from '@dealstack/frontend/app/pages/Project/DealEventLog/components/DealEventLogTable/DealEventStatusCell.tsx';
import { formatEventTypeLabel } from '@dealstack/frontend/app/pages/Project/DealEventLog/utils/dealEventLogType.utils.ts';
import { DealEventLogColumn } from '@dealstack/frontend/app/pages/Project/DealEventLog/utils/dealEventLogQuery.utils.ts';
import {
	DealEventFilterOption,
	getDealEventAccessTypeOptions,
	getDealEventCategoryOptions,
	getDealEventChannelOptions,
	getDealEventStatusOptions,
} from '@dealstack/frontend/app/pages/Project/DealEventLog/utils/dealEventLogFilterOptions.utils.ts';

type Translate = TranslateFunction<I18Namespaces.CAP_MANAGE>;

export interface DealEventLogColumnOptions {
	actorOptions: DealEventFilterOption[];
	recipientOptions: DealEventFilterOption[];
	recipientAsync: AsyncFilterMultiSelectProps;
	subjectOptions: DealEventFilterOption[];
	subjectAsync: AsyncFilterMultiSelectProps;
	securityOptions: DealEventFilterOption[];
}

const getHeaders = (tab: DealEventLogTab, t: Translate) =>
	tab === DealEventLogTab.MESSAGING
		? {
				event: t('deal_event_log.table.message'),
				type: t('deal_event_log.table.channel'),
				actor: t('deal_event_log.table.sender'),
			}
		: {
				event: t('deal_event_log.table.event'),
				type: t('deal_event_log.table.type'),
				actor: t('deal_event_log.table.actor'),
			};

const formatChannel = (event: DealEventTct, t: Translate): string =>
	event.source === DealEventSource.EMAIL_SENT
		? t('deal_event_log.filter.channel.email')
		: event.type;

/** Options the feed can actually narrow by on this tab; an empty list leaves the column unfiltered. */
const getTypeOptions = (tab: DealEventLogTab, t: Translate): DealEventFilterOption[] => {
	switch (tab) {
		case DealEventLogTab.ALL_EVENTS:
			return getDealEventCategoryOptions(t);
		case DealEventLogTab.TEAM_AND_ACCESS:
			return getDealEventAccessTypeOptions(t);
		case DealEventLogTab.MESSAGING:
			return getDealEventChannelOptions(t);
		default:
			return [];
	}
};

interface EntityColumn {
	header: string;
	value: (event: DealEventTct) => string;
	options?: DealEventFilterOption[];
	async?: AsyncFilterMultiSelectProps;
}

const getEntityColumn = (
	tab: DealEventLogTab,
	t: Translate,
	{
		recipientOptions,
		recipientAsync,
		subjectOptions,
		subjectAsync,
		securityOptions,
	}: DealEventLogColumnOptions
): EntityColumn | undefined => {
	switch (tab) {
		case DealEventLogTab.INVESTOR_ACTIVITY:
			return {
				header: t('deal_event_log.table.participant'),
				value: (event) => event.participant?.participantName ?? '',
			};
		case DealEventLogTab.CAPITAL_EVENTS:
			return {
				header: t('deal_event_log.table.security'),
				value: (event) => event.capital?.securityName ?? '',
				options: securityOptions,
			};
		case DealEventLogTab.TEAM_AND_ACCESS:
			return {
				header: t('deal_event_log.table.user'),
				value: (event) => event.access?.targetName ?? event.actor.displayName ?? '',
				// Read from the events, not the deal's team: a revoked member is still named here.
				options: subjectOptions,
				async: subjectAsync,
			};
		case DealEventLogTab.MESSAGING:
			return {
				header: t('deal_event_log.table.recipient'),
				value: (event) =>
					event.message?.recipientName ?? event.message?.recipientEmail ?? '',
				options: recipientOptions,
				async: recipientAsync,
			};
		default:
			return undefined;
	}
};

const multiSelectProps = (
	options: DealEventFilterOption[],
	async?: AsyncFilterMultiSelectProps
) => ({
	filterVariant: 'multi-select',
	mantineFilterMultiSelectProps: { data: options, ...async },
});

const singleSelectProps = (options: DealEventFilterOption[]) => ({
	filterVariant: 'select',
	mantineFilterSelectProps: { data: options },
});

export const getDealEventLogColumns = (
	tab: DealEventLogTab,
	t: Translate,
	columnOptions: DealEventLogColumnOptions
): ExtendedColumnDef<DealEventTct>[] => {
	const headers = getHeaders(tab, t);
	const entity = getEntityColumn(tab, t, columnOptions);
	const typeOptions = getTypeOptions(tab, t);

	return [
		{
			id: 'expander',
			header: '',
			enableSorting: false,
			showFilter: false,
			minSize: 40,
			maxSize: 48,
			cell: ({ row }) =>
				!row.getCanExpand() ? null : (
					<Flex align="center" justify="center" w="100%">
						<ActionIcon
							variant="transparent"
							aria-label={t('deal_event_log.table.toggle_detail')}
							onClick={(clickEvent) => {
								clickEvent.stopPropagation();
								row.toggleExpanded();
							}}
						>
							<Icon
								variant={row.getIsExpanded() ? 'caret-down' : 'caret-right'}
								size="lg"
								fillStroke={true}
								color="#000"
							/>
						</ActionIcon>
					</Flex>
				),
		},
		createTextColumn<DealEventTct>({
			id: DealEventLogColumn.DESCRIPTION,
			accessorKey: 'description',
			headerText: headers.event,

			showFilter: false,
			cell: ({ row }) => (
				<Text size="sm" fw={500} truncate>
					{formatDealEventHeading(row.original, t)}
				</Text>
			),
		}),
		createTextColumn<DealEventTct>({
			id: DealEventLogColumn.TYPE,
			headerText: headers.type,
			filterText: headers.type,
			showFilter: typeOptions.length > 0,
			...multiSelectProps(typeOptions),
			accessorFn: (event) =>
				tab === DealEventLogTab.MESSAGING
					? formatChannel(event, t)
					: formatEventTypeLabel(event, t),
		}),
		createTextColumn<DealEventTct>({
			id: DealEventLogColumn.ACTOR,
			headerText: headers.actor,
			filterText: headers.actor,
			showFilter: true,
			...singleSelectProps(columnOptions.actorOptions),
			accessorFn: (event) => formatActorName(event.actor, t),
		}),
		...(entity
			? [
					createTextColumn<DealEventTct>({
						id: DealEventLogColumn.ENTITY,
						headerText: entity.header,
						filterText: entity.header,
						showFilter: !!entity.options,
						...multiSelectProps(entity.options ?? [], entity.async),
						accessorFn: entity.value,
					}),
				]
			: []),
		createTextColumn<DealEventTct>({
			id: DealEventLogColumn.STATUS,
			accessorKey: 'status',
			headerText: t('deal_event_log.table.status'),
			filterText: t('deal_event_log.table.status'),
			showFilter: true,
			...multiSelectProps(getDealEventStatusOptions(tab, t)),
			cell: ({ row }) => <DealEventStatusCell status={row.original.status} />,
		}),
		createDateColumn<DealEventTct>({
			id: DealEventLogColumn.OCCURRED_AT,
			accessorKey: 'occurredAt',
			headerText: t('deal_event_log.table.timestamp'),
			dateFormat: DATE_FORMATS.DATE_TIME,
			showFilter: true,
			filterValueType: 'date-range',
			filterVariant: 'date-range',
			filter: ({ column }) => (
				<DateRangePickerFilter
					column={column}
					placeholder={t('deal_event_log.table.timestamp')}
				/>
			),
		}),
	];
};
